/**
 * Self-Healing Auto Runner - Timer management and cycle execution guard.
 *
 * GOVERNANCE (Phase 2-2.5 Containment):
 *   Sets healingCycleActive = true before each cycle and false after,
 *   structurally preventing PROJECT_CHANGE triggers from re-queueing
 *   during an active healing cycle.
 *
 *   If SELF_HEALING_EXECUTION_ENABLED = false, emits selfHealing:suspended
 *   on start and logs the suspension reason. Detection and proposals continue.
 */

import { runSelfHealingCycle }                    from "./orchestrator";
import { setHealingCycleActive }                  from "./triggerEngine";
import { SELF_HEALING_EXECUTION_ENABLED,
         SUSPENSION_REASON }                       from "./governanceConfig";
import { eventBus }                               from "@/runtime/eventBus";
import { logger }                                 from "@/system/logger";

/* =========================================================
   STATE
   ========================================================= */

let interval: ReturnType<typeof setInterval> | null = null;
let isRunning    = false;
let cycleCount   = 0;
let lastRunAt:   number | null = null;
let failureCount = 0;

/* =========================================================
   CONFIG
   ========================================================= */

const MAX_FAILURES  = 5;
const BASE_INTERVAL = 10_000;
const MAX_INTERVAL  = 60_000;
const MAX_CYCLE_TIME = 15_000;

/* =========================================================
   INTERNAL HELPERS
   ========================================================= */

function getAdaptiveInterval(): number {
  if (failureCount === 0) return BASE_INTERVAL;
  return Math.min(BASE_INTERVAL * (1 + failureCount), MAX_INTERVAL);
}

async function runWithTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeout: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeout = setTimeout(
      () => reject(new Error("Auto-heal cycle timeout")),
      timeoutMs,
    );
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    clearTimeout(timeout!);
  }
}

function isSystemHealthy(): boolean {
  return failureCount < MAX_FAILURES;
}

/* =========================================================
   CORE EXECUTION
   ========================================================= */

export async function runAutoHealingOnce(): Promise<void> {
  if (isRunning) {
    logger.debug("[AutoHealRunner] Already running, skipping");
    return;
  }

  if (!isSystemHealthy()) {
    logger.error("[AutoHealRunner] Blocked - failure circuit open");
    return;
  }

  isRunning = true;
  // ── STRUCTURAL LOOP GUARD: activated before cycle, released in finally ──
  setHealingCycleActive(true);

  try {
    logger.debug("[AutoHealRunner] Starting healing cycle");
    await runWithTimeout(runSelfHealingCycle(), MAX_CYCLE_TIME);

    cycleCount++;
    lastRunAt    = Date.now();
    failureCount = 0;

    logger.debug(`[AutoHealRunner] Cycle #${cycleCount} completed`);

  } catch (err: any) {
    failureCount++;
    logger.error("[AutoHealRunner] Cycle error", {
      message:      err?.message || String(err),
      failureCount,
    });
  } finally {
    isRunning = false;
    // Always release the loop guard, even on timeout or exception
    setHealingCycleActive(false);
  }
}

/* =========================================================
   START / STOP
   ========================================================= */

export function startAutoHealing(intervalMs = BASE_INTERVAL): void {
  if (interval) return;

  // Announce suspension status at start so callers and monitors know the mode
  if (!SELF_HEALING_EXECUTION_ENABLED) {
    eventBus.emit({ type: "selfHealing:suspended", reason: SUSPENSION_REASON });
    logger.warn(
      "[AutoHealRunner] Execution SUSPENDED - detection and proposals only. " +
      "See governanceConfig.ts for re-authorization requirements."
    );
  } else {
    logger.info("[AutoHealRunner] Auto-healing started (execution enabled)");
  }

  interval = setInterval(async () => {
    if (isRunning) {
      logger.debug("[AutoHealRunner] Previous cycle still running, skipping");
      return;
    }

    if (!isSystemHealthy()) {
      logger.error("[AutoHealRunner] Disabled - too many consecutive failures");
      stopAutoHealing();
      return;
    }

    await runAutoHealingOnce();

    // Adaptive interval: slow down after failures
    const newInterval = getAdaptiveInterval();
    if (intervalMs !== newInterval) {
      logger.debug(`[AutoHealRunner] Adjusting interval to ${newInterval}ms`);
      stopAutoHealing();
      startAutoHealing(newInterval);
    }
  }, intervalMs);
}

export function stopAutoHealing(): void {
  if (interval) {
    clearInterval(interval);
    interval = null;
    logger.info("[AutoHealRunner] Stopped");
  }
}

/* =========================================================
   RESET
   ========================================================= */

export function resetAutoHealing(): void {
  failureCount = 0;
  cycleCount   = 0;
  lastRunAt    = null;
  isRunning    = false;
  logger.debug("[AutoHealRunner] State reset");
}

/* =========================================================
   STATUS / OBSERVABILITY
   ========================================================= */

export function getAutoHealingStatus(): {
  running:            boolean;
  isRunning:          boolean;
  cycleCount:         number;
  lastRunAt:          number | null;
  failureCount:       number;
  nextInterval:       number;
  healthy:            boolean;
  executionEnabled:   boolean;
} {
  return {
    running:          !!interval,
    isRunning,
    cycleCount,
    lastRunAt,
    failureCount,
    nextInterval:     getAdaptiveInterval(),
    healthy:          isSystemHealthy(),
    executionEnabled: SELF_HEALING_EXECUTION_ENABLED,
  };
}
